import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MapPin, Plus, Trash2, Clock } from 'lucide-react';

const formatDuration = (mins) => {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

export const LocationTimesCard = ({ locationTimes, onAdd, onDelete }) => {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
          <div className="min-w-0">
            <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 flex-shrink-0" />
              Tätigkeiten für andere Standorte
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Nachweis – wird nicht zur Arbeitszeit addiert
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={onAdd}
            className="w-full sm:w-auto flex-shrink-0"
            data-testid="add-location-time-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Eintrag hinzufügen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {locationTimes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Noch keine Einträge für heute erfasst
          </p>
        ) : (
          <div className="space-y-2">
            {locationTimes.map((lt) => (
              <div
                key={lt.id}
                className={`flex items-start sm:items-center justify-between gap-2 p-3 rounded-lg ${lt.is_external ? 'bg-orange-50 border border-orange-200 dark:bg-orange-900/10 dark:border-orange-900/40' : 'bg-secondary/30'}`}
                data-testid={`location-time-${lt.id}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <MapPin className={`h-4 w-4 mt-1 flex-shrink-0 ${lt.is_external ? 'text-orange-500' : 'text-muted-foreground'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium truncate">{lt.location_name}</p>
                      {lt.is_external && (
                        <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                          Extern
                        </Badge>
                      )}
                      {lt.category && (
                        <Badge variant="secondary" className="text-xs">
                          {lt.category}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      {lt.start_time && lt.end_time ? (
                        <>
                          <span className="font-mono">{lt.start_time}–{lt.end_time}</span>
                          <span>·</span>
                          <span>{formatDuration(lt.duration_minutes)}</span>
                        </>
                      ) : (
                        <span>{lt.duration_minutes} Min</span>
                      )}
                    </div>
                    {lt.notes && <p className="text-xs text-muted-foreground italic mt-0.5 truncate">{lt.notes}</p>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 flex-shrink-0"
                  onClick={() => onDelete(lt.id)}
                  data-testid={`delete-location-time-${lt.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="pt-2 border-t flex justify-between text-sm">
              <span className="text-muted-foreground">Gesamt-Dauer (Nachweis):</span>
              <span className="font-medium font-mono">
                {formatDuration(locationTimes.reduce((sum, lt) => sum + (lt.duration_minutes || 0), 0)) || '0min'}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
